import React from 'react';
import {
    BarChart3, Fuel, Check, X, HelpCircle, Flame
} from 'lucide-react';
import {
    PieChart, Pie, Cell, ResponsiveContainer, Tooltip
} from 'recharts';
import HelpTooltip, { helpContent } from '../HelpTooltip';

const DashboardCharts = ({
    scopeData,
    categoryData,
    getShortPeriodLabel,
    categoryColors,
    filteredEmissions,
    categories,
    compliancePercentage,
    emissions
}) => {
    // Color palette for specific categories in Top Emission Sources
    const topSourcesCategoryColors = {
        'Electricity - Grid': '#2563eb',
        'Fuel - Diesel': '#0d5c2e',
        'Fuel - Petrol': '#16a34a',
        'Natural Gas': '#dc2626',
        'Waste - Landfill': '#d97706',
        'Water - Municipal': '#0891b2'
    };

    const scopeColors = {
        'Scope 1': '#E76F51',
        'Scope 2': '#F4A261'
    };

    // Top Emission Sources Calculation
    const getTopSources = () => {
        const categoryTotals = {};
        filteredEmissions.forEach(e => {
            const cat = categories.find(c => c.value === e.category);
            if (cat && e.co2_equivalent) {
                const label = cat.label;
                categoryTotals[label] = (categoryTotals[label] || 0) + e.co2_equivalent;
            }
        });

        return Object.entries(categoryTotals)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 5)
            .map(([name, value]) => ({ name, value: parseFloat(value.toFixed(2)) }));
    };

    const topSources = getTopSources();
    const maxValue = topSources.length > 0 ? Math.max(...topSources.map(s => s.value)) : 0;

    return (
        <div className="space-y-8">
            <div className="grid md:grid-cols-2 gap-6">
                {/* Emissions by Scope Pie Chart */}
                <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
                    <h2 className="text-xl font-semibold text-[#003B5C] mb-4 text-center" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        Emissions by Scope (%)
                    </h2>
                    {scopeData.length > 0 ? (
                        <div className="flex flex-col items-center">
                            <div className="w-full h-64" style={{ minHeight: '256px', minWidth: '200px' }}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie
                                            data={scopeData}
                                            cx="50%"
                                            cy="50%"
                                            outerRadius={90}
                                            innerRadius={0}
                                            paddingAngle={3}
                                            dataKey="value"
                                            stroke="#fff"
                                            strokeWidth={3}
                                            isAnimationActive={true}
                                            animationBegin={0}
                                            animationDuration={800}
                                            animationEasing="ease-out"
                                            label={({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
                                                const RADIAN = Math.PI / 180;
                                                const radius = innerRadius + (outerRadius - innerRadius) * 0.6;
                                                const x = cx + radius * Math.cos(-midAngle * RADIAN);
                                                const y = cy + radius * Math.sin(-midAngle * RADIAN);
                                                return percent > 0.05 ? (
                                                    <text
                                                        x={x}
                                                        y={y}
                                                        fill="#fff"
                                                        textAnchor="middle"
                                                        dominantBaseline="central"
                                                        style={{
                                                            fontSize: '14px',
                                                            fontWeight: 'bold',
                                                            textShadow: '0 1px 2px rgba(0,0,0,0.3)'
                                                        }}
                                                    >
                                                        {`${(percent * 100).toFixed(0)}%`}
                                                    </text>
                                                ) : null;
                                            }}
                                            labelLine={false}
                                        >
                                            {scopeData.map((entry, index) => (
                                                <Cell
                                                    key={`scope-cell-${index}`}
                                                    fill={scopeColors[entry.name] || entry.color}
                                                    style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))' }}
                                                />
                                            ))}
                                        </Pie>
                                        <Tooltip
                                            formatter={(value, name) => [`${value.toFixed(2)} tCO₂-e`, name]}
                                            contentStyle={{
                                                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                                borderRadius: '12px',
                                                border: '1px solid #e2e8f0',
                                                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                                padding: '12px 16px'
                                            }}
                                            itemStyle={{ color: '#334155', fontWeight: '500' }}
                                        />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                            <div className="flex flex-wrap gap-4 justify-center mt-3 pt-3 border-t border-slate-100 w-full">
                                {scopeData.map((scope) => (
                                    <div key={scope.name} className="flex items-center space-x-2">
                                        <div
                                            className="w-3 h-3 rounded-sm"
                                            style={{ backgroundColor: scopeColors[scope.name] || scope.color }}
                                        />
                                        <span className="text-xs font-medium text-slate-700">{scope.name}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : (
                        <div className="text-center py-12 text-slate-500">
                            <BarChart3 className="w-10 h-10 mx-auto mb-3 text-slate-300" />
                            <p className="text-sm">No scope data for {getShortPeriodLabel()}</p>
                        </div>
                    )}
                </div>

                {/* Emissions by Category Pie Chart */}
                <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
                    <h2 className="text-xl font-semibold text-[#003B5C] mb-4 text-center" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        Emissions by Category (%)
                    </h2>
                    {categoryData.length > 0 ? (
                        <div className="flex flex-col items-center">
                            <div className="w-full h-64" style={{ minHeight: '256px', minWidth: '200px' }}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie
                                            data={categoryData}
                                            cx="50%"
                                            cy="50%"
                                            outerRadius={90}
                                            innerRadius={0}
                                            paddingAngle={3}
                                            dataKey="value"
                                            stroke="#fff"
                                            strokeWidth={3}
                                            isAnimationActive={true}
                                            animationBegin={0}
                                            animationDuration={800}
                                            animationEasing="ease-out"
                                            label={({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
                                                const RADIAN = Math.PI / 180;
                                                const radius = innerRadius + (outerRadius - innerRadius) * 0.6;
                                                const x = cx + radius * Math.cos(-midAngle * RADIAN);
                                                const y = cy + radius * Math.sin(-midAngle * RADIAN);
                                                return percent > 0.05 ? (
                                                    <text
                                                        x={x}
                                                        y={y}
                                                        fill="#fff"
                                                        textAnchor="middle"
                                                        dominantBaseline="central"
                                                        style={{
                                                            fontSize: '14px',
                                                            fontWeight: 'bold',
                                                            textShadow: '0 1px 2px rgba(0,0,0,0.3)'
                                                        }}
                                                    >
                                                        {`${(percent * 100).toFixed(0)}%`}
                                                    </text>
                                                ) : null;
                                            }}
                                            labelLine={false}
                                        >
                                            {categoryData.map((entry, index) => (
                                                <Cell
                                                    key={`cat-cell-${index}`}
                                                    fill={categoryColors[index % categoryColors.length]}
                                                    style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))' }}
                                                />
                                            ))}
                                        </Pie>
                                        <Tooltip
                                            formatter={(value, name) => [`${value.toFixed(2)} tCO₂-e`, name]}
                                            contentStyle={{
                                                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                                borderRadius: '12px',
                                                border: '1px solid #e2e8f0',
                                                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                                padding: '12px 16px'
                                            }}
                                            itemStyle={{ color: '#334155', fontWeight: '500' }}
                                        />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                            <div className="flex flex-wrap gap-3 justify-center mt-3 pt-3 border-t border-slate-100 w-full">
                                {categoryData.slice(0, 5).map((cat, index) => (
                                    <div key={cat.name} className="flex items-center space-x-2">
                                        <div
                                            className="w-3 h-3 rounded-sm"
                                            style={{ backgroundColor: categoryColors[index % categoryColors.length] }}
                                        />
                                        <span className="text-xs font-medium text-slate-700 truncate max-w-[100px]" title={cat.name}>{cat.name}</span>
                                    </div>
                                ))}
                                {categoryData.length > 5 && (
                                    <span className="text-xs text-slate-500">+{categoryData.length - 5} more</span>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className="text-center py-12 text-slate-500">
                            <BarChart3 className="w-10 h-10 mx-auto mb-3 text-slate-300" />
                            <p className="text-sm">No category data for {getShortPeriodLabel()}</p>
                        </div>
                    )}
                </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
                {/* Top Emission Sources Chart */}
                <div className="bg-white rounded-2xl p-8 shadow-sm border border-slate-100">
                    <h2 className="text-xl font-semibold text-[#003B5C] mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        Top Emission Sources
                    </h2>
                    {topSources.length === 0 ? (
                        <div className="text-center py-12 text-slate-500">
                            <Fuel className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                            <p>No emission sources tracked yet</p>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {topSources.map((source, index) => {
                                const percentage = maxValue > 0 ? (source.value / maxValue) * 100 : 0;
                                const color = topSourcesCategoryColors[source.name] || ['#003B5C', '#74A57F', '#E9C46A', '#E76F51', '#2A9D8F'][index % 5];
                                return (
                                    <div key={source.name} className="group">
                                        <div className="flex justify-between items-center mb-1">
                                            <span className="text-sm font-medium text-slate-700 truncate max-w-[180px]">
                                                {source.name}
                                            </span>
                                            <span className="text-sm font-bold text-slate-600">
                                                {source.value.toFixed(2)} tCO₂-e
                                            </span>
                                        </div>
                                        <div className="h-6 bg-slate-100 rounded overflow-hidden">
                                            <div
                                                className="h-full rounded group-hover:brightness-110"
                                                style={{
                                                    width: `${percentage}%`,
                                                    backgroundColor: color,
                                                    transition: 'width 0.8s ease-out'
                                                }}
                                            />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Logged Data by Category - With Scope Indicators */}
                <div className="bg-white rounded-2xl p-8 shadow-sm border border-slate-100">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-xl font-semibold text-[#003B5C]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                            Logged Data by Category
                        </h2>
                    </div>

                    {/* Scope Legend */}
                    <div className="mb-4 p-3 bg-slate-50 rounded-lg">
                        <p className="text-xs font-medium text-slate-500 mb-2">Emission Scopes (GHG Protocol)</p>
                        <div className="flex flex-wrap gap-3 text-xs">
                            <span className="flex items-center">
                                <span className="w-2 h-2 rounded-full bg-orange-500 mr-1.5"></span>
                                <span className="text-slate-600"><strong>Scope 1:</strong> Direct emissions</span>
                                <HelpTooltip {...helpContent.scope1} size="sm" />
                            </span>
                            <span className="flex items-center">
                                <span className="w-2 h-2 rounded-full bg-amber-500 mr-1.5"></span>
                                <span className="text-slate-600"><strong>Scope 2:</strong> Purchased electricity</span>
                                <HelpTooltip {...helpContent.scope2} size="sm" />
                            </span>
                        </div>
                    </div>

                    <div className="space-y-3">
                        {/* Core Scopes (1 & 2) - Required */}
                        <div className="mb-2">
                            <p className="text-xs font-medium text-slate-500 mb-2">Core Categories (Required for Compliance)</p>
                            {[
                                { item: 'Electricity usage logged', scope: 'Scope 2', color: 'amber', category: 'electricity' },
                                { item: 'Fuel consumption logged', scope: 'Scope 1', color: 'orange', category: 'fuel' },
                                { item: 'Natural gas logged', scope: 'Scope 1', color: 'orange', category: 'natural_gas' },
                            ].map((check, index) => {
                                const hasData = emissions?.some(e =>
                                    e.category === check.category ||
                                    e.category?.startsWith(check.category.split('_')[0])
                                );
                                return (
                                    <div
                                        key={index}
                                        className={`flex items-center justify-between p-3 rounded-lg transition-colors ${hasData ? 'bg-[#74A57F]/10' : 'bg-[#F8F9FA] hover:bg-[#EDF6F9]'}`}
                                    >
                                        <div className="flex items-center space-x-2">
                                            <span className={`w-2 h-2 rounded-full bg-${check.color}-500`}></span>
                                            <span className="text-slate-700 text-sm">{check.item}</span>
                                            <span className={`text-[10px] px-1.5 py-0.5 rounded bg-${check.color}-100 text-${check.color}-700`}>
                                                {check.scope}
                                            </span>
                                        </div>
                                        {hasData ? (
                                            <Check className="w-5 h-5 text-[#74A57F]" />
                                        ) : (
                                            <X className="w-5 h-5 text-slate-400" />
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {compliancePercentage === 100 && (
                        <div className="mt-4 p-4 bg-[#74A57F]/10 border border-[#74A57F]/20 rounded-lg">
                            <p className="text-sm text-[#74A57F] font-medium flex items-center">
                                <Check className="w-4 h-4 mr-2" />
                                All categories tracked! Ready for AASB S2 Climate-related Disclosures compliance.
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default DashboardCharts;
